import { useState, useCallback } from 'react';
import useWebSocket, { ReadyState } from 'react-use-websocket';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, TextField, Button, Alert, CircularProgress, Paper,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { SearchField } from '../Checkout';
import { validateClaimForm } from '../../claim-helpers-shared';

const ClaimCreateForm = ({ posConfig }) => {
    const [cookies] = useCookies(['secret', 'station', 'staff']);
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        kdnr: '',
        kdnrLabel: '',
        absender: '',
        sendungsnummer: '',
        packstuecknummer: '',
        trackingnummer: '',
        id_nummer: '',
        zustelldatum: '',
        spedition: '',
        empfaenger: '',
        kommentar: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const socketUrl = posConfig?.socketUrl || process.env.REACT_APP_WEBSOCKET_URL;

    const { sendMessage, readyState } = useWebSocket(socketUrl, {
        queryParams: { station: cookies.station, secret: cookies.secret, staff: cookies.staff },
        onMessage: (message) => handleWebsocketEvent(message),
        shouldReconnect: () => true,
        retryOnError: true,
    });

    const handleWebsocketEvent = useCallback((message) => {
        try {
            const msg = JSON.parse(message.data);
            if (msg.task === 'respCreateClaimInternal') {
                setSubmitting(false);
                if (msg.status === 'succeeded') {
                    setSuccess(`Suchanfrage erstellt: ${msg.claimId}`);
                    setError(null);
                    setTimeout(() => navigate('..'), 1500);
                } else {
                    setError(msg.message || 'Fehler beim Erstellen.');
                }
            }
        } catch (e) {
            console.error('WS parse error:', e);
        }
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleKdnrSelect = (kdnr) => {
        setFormData((prev) => ({ ...prev, kdnr: String(kdnr) }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!formData.kdnr.trim()) {
            setError('Bitte einen Kunden auswählen.');
            return;
        }

        const validationError = validateClaimForm(formData);
        if (validationError) {
            setError(validationError);
            return;
        }

        setSubmitting(true);

        const payload = {
            task: 'createClaimInternal',
            station: cookies.station,
            staff: cookies.staff,
            kdnr: Number(formData.kdnr.trim()),
            absender: formData.absender.trim(),
            sendungsnummer: formData.sendungsnummer.trim(),
        };

        if (formData.packstuecknummer.trim()) payload.packstuecknummer = formData.packstuecknummer.trim();
        if (formData.trackingnummer.trim()) payload.trackingnummer = formData.trackingnummer.trim();
        if (formData.id_nummer.trim()) payload.id_nummer = formData.id_nummer.trim();
        if (formData.zustelldatum.trim()) payload.zustelldatum = formData.zustelldatum.trim();
        if (formData.spedition.trim()) payload.spedition = formData.spedition.trim();
        if (formData.empfaenger.trim()) payload.empfaenger = formData.empfaenger.trim();
        if (formData.kommentar.trim()) payload.kommentar = formData.kommentar.trim();

        sendMessage(JSON.stringify(payload));
    };

    const isConnected = readyState === ReadyState.OPEN;

    return (
        <Box>
            <Box sx={{ px: 0, py: 0 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Button
                        size="small"
                        startIcon={<ArrowBackIcon sx={{ fontSize: 16 }} />}
                        onClick={() => navigate('..')}
                        sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, color: '#555' }}
                    >
                        Zurück
                    </Button>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Suchanfrage erstellen
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

                <Paper sx={{ p: 4 }}>
                <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600 }}>

                    {/* Kundensuche via SearchField aus Checkout */}
                    <SearchField
                        posConfig={posConfig}
                        onSelect={handleKdnrSelect}
                        PaperComponent={Box}
                        paperSx={{ p: 0 }}
                        noAutofocus
                    />
                    {formData.kdnr && (
                        <Alert severity="info" sx={{ py: 0, borderRadius: 2 }}>
                            Ausgewählter Kunde: <strong>{formData.kdnr}</strong>
                        </Alert>
                    )}

                    <TextField label="Sendungsnummer" name="sendungsnummer" value={formData.sendungsnummer}
                        onChange={handleChange} required size="small"
                        helperText="Die Sendungsnummer finden Sie in der Versandbestätigung Ihres Absenders." />
                    <TextField label="Absender" name="absender" value={formData.absender}
                        onChange={handleChange} required size="small"
                        helperText="Wer ist der Absender? (z.B. Amazon, Zalando, Otto)" />

                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#999', mt: 1 }}>Weitere Angaben (optional)</Typography>

                    <TextField label="Packstücknummer" name="packstuecknummer" value={formData.packstuecknummer}
                        onChange={handleChange} size="small" />
                    <TextField label="Trackingnummer" name="trackingnummer" value={formData.trackingnummer}
                        onChange={handleChange} size="small" />
                    <TextField label="ID-Nummer" name="id_nummer" value={formData.id_nummer}
                        onChange={handleChange} size="small" />
                    <TextField label="Zustelldatum und Uhrzeit" name="zustelldatum" value={formData.zustelldatum}
                        onChange={handleChange} size="small" type="datetime-local" InputLabelProps={{ shrink: true }}
                        helperText="Wann hat die Spedition die Sendung zugestellt?" />
                    <TextField label="Spedition" name="spedition" value={formData.spedition}
                        onChange={handleChange} size="small"
                        helperText="Welcher Lieferdienst? (z.B. DHL, UPS, DPD, Post)" />
                    <TextField label="Empfänger laut Spedition" name="empfaenger" value={formData.empfaenger}
                        onChange={handleChange} size="small"
                        helperText="Wurde ein falscher Empfänger/Kundennummer angegeben?" />
                    <TextField label="Kommentar" name="kommentar" value={formData.kommentar}
                        onChange={handleChange} multiline rows={3} size="small" />

                    <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Button type="submit" variant="contained" disableElevation disabled={submitting || !isConnected}
                            sx={{
                                borderRadius: 2, textTransform: 'none', fontSize: 13, fontWeight: 600,
                                bgcolor: '#1a1a1a', '&:hover': { bgcolor: '#333' },
                            }}>
                            {submitting ? <CircularProgress size={24} /> : 'Suchanfrage erstellen'}
                        </Button>
                        <Button variant="outlined" onClick={() => navigate('..')} disabled={submitting}
                            sx={{
                                borderRadius: 2, textTransform: 'none', fontSize: 13, fontWeight: 600,
                                borderColor: '#ddd', color: '#1a1a1a',
                            }}>
                            Abbrechen
                        </Button>
                    </Box>
                </Box>
                </Paper>
            </Box>
        </Box>
    );
};

export default ClaimCreateForm;
